import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { AnimatedPage } from '@theonefamily/ui';
import { useAuth } from './hooks/useAuth';

function HubSSOCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const API_BASE = import.meta.env['VITE_API_URL'] ?? '/api';

  useEffect(() => {
    if (!token) { navigate('/', { replace: true }); return; }
    fetch(`${API_BASE}/auth/portal-sso?token=${encodeURIComponent(token)}`, { credentials: 'include' })
      .then(r => r.ok ? navigate('/', { replace: true }) : navigate('/?error=sso_failed', { replace: true }))
      .catch(() => navigate('/?error=sso_failed', { replace: true }));
  }, [token, API_BASE, navigate]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'system-ui' }}>
      <p>Signing in...</p>
    </div>
  );
}

import AppLayout from './components/AppLayout';
import DashboardPage from './pages/DashboardPage';
import ConnectionsPage from './pages/ConnectionsPage';
import NewConnectionPage from './pages/NewConnectionPage';
import ImportPage from './pages/ImportPage';
import MigrationsPage from './pages/MigrationsPage';
import MigrationDetailPage from './pages/MigrationDetailPage';
import ExportPage from './pages/ExportPage';
import SettingsPage from './pages/SettingsPage';
import APIKeysPage from './pages/APIKeysPage';
import CompanyAPIKeysPage from './pages/CompanyAPIKeysPage';
import ConnectorImportPage from './pages/ConnectorImportPage';
import WebhooksPage from './pages/WebhooksPage';
import WebhookDetailPage from './pages/WebhookDetailPage';
import InboundWebhooksPage from './pages/InboundWebhooksPage';
import FlowsPage from './pages/FlowsPage';
import FlowBuilderPage from './pages/FlowBuilderPage';
import TemplateGalleryPage from './pages/TemplateGalleryPage';
import SyncProfilesPage from './pages/SyncProfilesPage';
import AIMapperPage from './pages/AIMapperPage';
import ErrorRecoveryPage from './pages/ErrorRecoveryPage';
import SyncMonitoringPage from './pages/SyncMonitoringPage';
import MarketplacePage from './pages/MarketplacePage';
import IntegrationsPage from './pages/IntegrationsPage';
import BulkOperationsPage from './pages/BulkOperationsPage';
import IntelligencePage from './pages/IntelligencePage';
import AnalyticsPage from './pages/AnalyticsPage';
import TransformStudioPage from './pages/TransformStudioPage';
import AdvisorPage from './pages/AdvisorPage';
import GraphQLPage from './pages/GraphQLPage';
import CustomConnectorsPage from './pages/CustomConnectorsPage';
import OpenApiDocsPage from './pages/OpenApiDocsPage';
import SDKPage from './pages/SDKPage';
import RPAPage from './pages/RPAPage';
import ConversationAgentsPage from './pages/ConversationAgentsPage';
import ConflictsPage from './pages/ConflictsPage';
import AuditPage from './pages/AuditPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'system-ui' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    const hubUrl = import.meta.env.VITE_HUB_URL || 'https://hub.theonestack.com';
    window.location.href = `${hubUrl}/login?redirect=bridge`;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'system-ui', flexDirection: 'column', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>The One Bridge</h1>
        <p style={{ color: '#666' }}>Redirecting to The One Hub for sign in...</p>
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <AnimatedPage>
      <Routes>
          <Route path="/auth/portal-sso" element={<HubSSOCallback />} />
          <Route element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/connections/new" element={<NewConnectionPage />} />
            <Route path="/connections/:connectionId/import" element={<ConnectorImportPage />} />
            <Route path="/import" element={<ImportPage />} />
            <Route path="/migrations" element={<MigrationsPage />} />
            <Route path="/migrations/:id" element={<MigrationDetailPage />} />
            <Route path="/export" element={<ExportPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/api-keys" element={<APIKeysPage />} />
            <Route path="/company-api-keys" element={<CompanyAPIKeysPage />} />
            <Route path="/webhooks" element={<WebhooksPage />} />
            <Route path="/webhooks/:id" element={<WebhookDetailPage />} />
            <Route path="/inbound-webhooks" element={<InboundWebhooksPage />} />
            <Route path="/flows" element={<FlowsPage />} />
            <Route path="/flows/:id" element={<FlowBuilderPage />} />
            <Route path="/templates" element={<TemplateGalleryPage />} />
            <Route path="/sync" element={<SyncProfilesPage />} />
            <Route path="/ai-mapper" element={<AIMapperPage />} />
            <Route path="/errors" element={<ErrorRecoveryPage />} />
            <Route path="/monitoring" element={<SyncMonitoringPage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/bulk" element={<BulkOperationsPage />} />
            <Route path="/intelligence" element={<IntelligencePage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/transforms" element={<TransformStudioPage />} />
            <Route path="/advisor" element={<AdvisorPage />} />
            <Route path="/graphql" element={<GraphQLPage />} />
            <Route path="/custom-connectors" element={<CustomConnectorsPage />} />
            <Route path="/api-docs" element={<OpenApiDocsPage />} />
            <Route path="/sdk" element={<SDKPage />} />
            <Route path="/rpa" element={<RPAPage />} />
            <Route path="/conversations" element={<ConversationAgentsPage />} />
            <Route path="/conflicts" element={<ConflictsPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </AnimatedPage>
  );
}
